import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { FormControl, Autocomplete } from "@mui/material";

import { joinByKeys } from "../../Js/Common";
import { getUsersDb } from "../../Layouts/Users/Services"; // ✅ uncomment

// import uniq from "lodash/uniq"; // not needed

const SalespersonDropdown = ({
  value,
  onChange,
  backgroundColor1,
  ukey,
  placeholder = "Select Sales Person",
  notAssignedOption = false,
}) => {
  // Redux state
  const { userInfo } = useSelector((state) => state.loggedInUserInfo);
  const roleTitle = userInfo.rolesInfo.title.toLowerCase();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  /** -----------------------------
   * Format users
   * ------------------------------ */
  const formatUsers = useCallback((data) => {
    return data.map((u) => ({
      label: joinByKeys(u.uname, ["firstname", "middlename", "lastname"], " "),
      value: u.uid,
      role_uid: u.role_uid,
      username: u.username,
    }));
  }, []);

  /** -----------------------------
   * Fetch Salespersons
   * ------------------------------ */
  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsersDb({
        query: {
          $select: ["uid", "role_uid", "uname", "username"],
          role_title: "salesperson",
          is_deleted: false,
          $limit: -1,
        },
      });

      let users = formatUsers(res.data || []);

      if (notAssignedOption) {
        users = [
          {
            label: "Not Assigned",
            value: "not_assigned",
            role_uid: null,
            username: "not_assigned",
          },
          ...users,
        ];
      }

      let self = [];
      let assigned = [];
      if(roleTitle === "salesperson") {
        self = users.filter((u) => u.value === userInfo.uid);
        assigned = users.filter((u) => userInfo.assigned_users?.uids?.includes(u.value));
        users = [...self, ...assigned];
      }
      setAllUsers(users);
    } catch (err) {
      console.error("Error fetching salespersons:", err);
    }
  }, [formatUsers]);

  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      return;
    }

    const found = allUsers.find((u) => u.value === value);
    setSelectedUser(found || null);
  }, [value, allUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** -----------------------------
   * Handle selection
   * ------------------------------ */
  const handleUsers = (val) => {
    setSelectedUser(val);
    if (onChange) {
      onChange(val);
    }
  };

  return (
    <Grid size={12}>
      <FormControl
        fullWidth
        size="small"
        sx={{
          backgroundColor: backgroundColor1 ? backgroundColor1 : "#FFFFFF",
          color: "#CCCCCC",
        }}
      >
        <Autocomplete
          options={allUsers}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(o, v) => o?.value === v?.value}
          value={selectedUser}
          onChange={(e, val) => handleUsers(val)}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              sx={{
                "& input::placeholder": {
                  color: "#888",
                  opacity: 0.6,
                },
              }}
            />
          )}
          size="small"
          key={ukey}
        />
      </FormControl>
    </Grid>
  );
};

export default SalespersonDropdown;
